import { Container } from 'pixi.js';
import { Scene } from '../app/SceneManager';
import { COLORS, DESIGN_W } from '../app/theme';
import { Panel, Button, makeText, sceneBackground, iconSprite, glassCard, Toggle } from '../ui/widgets';
import { Slider } from '../ui/Slider';
import { OptionPicker } from '../ui/OptionPicker';
import { ScrollList } from '../ui/ScrollList';
import { pageHeader, bottomBar, listHeight, enterFade, LIST_PAGE } from './common';
import type { AppContext } from '../app/AppContext';
import { GameState, GameName, RoomName } from '../net/enums';
import { MessageTag } from '../net/messageTags';
import {
  encodeSearchGame,
  decodeSearchGame,
  encodeJoinGame,
  encodeWatchGame,
  type SearchGameEntry,
} from '../net/codec/matchmaking';
import { betOrRoundsLabel, gameTimeLabel, gameTypeLabel, moneyFa, toPersianDigits } from '../util/persian';

const PRICE_MAX = 10_000_000;
const PRICE_STEP = 1_000;
const GAME_FILTERS = ['تمام بازی ها', 'حکم', 'تخته نرد', 'بیست و یک'];
const GAME_ROW_COLORS: Readonly<Record<GameName, number>> = {
  [GameName.Hokm]: COLORS.gameHokm,
  [GameName.Backgammon]: COLORS.gameBackgammon,
  [GameName.Blackjack]: COLORS.gameBlackjack,
};

/**
 * Browse & join existing tables (port of SearchGame.cs / WaitListItem.cs), re-skinned to the glass-card
 * style: the filters (show-running toggle, game-type dropdown, min/max bet sliders, counts) grouped in a
 * glass card, and compact room rows with a coin-marked price, a per-game icon, and a state-aware action
 * button — gold «شروع بازی» (+play) to JoinGame(42) a Wait table, secondary «جزئیات» (+eye) to
 * WatchGame(43) a Running one. All filtering is client-side; SearchGame(40) takes no arguments.
 */
export class SearchGameScene extends Scene {
  private entries: SearchGameEntry[] = [];
  private list!: ScrollList;
  private countText!: ReturnType<typeof makeText>;
  private showRunning = true;
  private gameFilter = 0; // 0 = all; else GameName = gameFilter - 1
  private minPrice = 0;
  private maxPrice = PRICE_MAX;
  private filtersExpanded = false;
  private built = false;

  constructor(private readonly ctx: AppContext) {
    super();
  }

  onEnter(): void {
    this.build();
    this.track(
      this.ctx.client.on(MessageTag.SearchGame, (r) => {
        this.entries = decodeSearchGame(r);
        this.render();
      }),
    );
    this.refresh();
  }

  /** Re-flow on viewport-height change; filter state lives in instance fields, so it survives the rebuild. */
  onResize(): void {
    this.build();
  }

  /** Build (or rebuild) all visuals against the live height, then render the current entries. */
  private build(): void {
    for (const ch of this.removeChildren()) ch.destroy({ children: true });
    this.addChild(sceneBackground());
    const enter: Container[] = [];
    const header = pageHeader('میزهای بازی', () => this.ctx.navigate(RoomName.MainMenu), this.ctx.iconTexture('search'));
    this.addChild(header);
    enter.push(header);

    // ── Filters glass card: show-running toggle + game-type dropdown, min/max sliders, live counts. ──
    const cardX = 24;
    const cardY = 112;
    const cardW = DESIGN_W - 48;
    const cardH = this.filtersExpanded ? 334 : 104;
    this.addChild(glassCard(cardX, cardY, cardW, cardH));

    const filterW = 350;
    const filter = new OptionPicker({
      width: filterW,
      height: 72,
      options: GAME_FILTERS,
      selected: this.gameFilter,
      onChange: (i) => {
        this.gameFilter = i;
        this.render();
      },
    });
    filter.position.set(cardX + cardW - 16 - filterW, cardY + 16);
    this.addChild(filter);
    enter.push(filter);

    const filterToggle = new Button(this.filtersExpanded ? 'بستن' : 'فیلتر', () => {
      this.filtersExpanded = !this.filtersExpanded;
      this.build();
    }, {
      width: 134,
      height: 72,
      fontSize: 24,
      fill: COLORS.panel,
      textColor: COLORS.text,
      icon: this.ctx.iconTexture('filter') ?? null,
      accessibleLabel: this.filtersExpanded ? 'بستن فیلترهای پیشرفته' : 'نمایش فیلترهای پیشرفته',
    });
    filterToggle.position.set(cardX + 16, cardY + 16);
    this.addChild(filterToggle);
    enter.push(filterToggle);

    if (this.filtersExpanded) {
      const toggle = new Toggle('نمایش میزهای در حال بازی', this.showRunning, (v) => {
        this.showRunning = v;
        this.render();
      });
      toggle.position.set(cardX + 28, cardY + 112);
      this.addChild(toggle);
      enter.push(toggle);

      this.rangeRow('کمترین ورودی', cardY + 166, this.minPrice, (v) => {
        this.minPrice = v;
        this.render();
      }, enter);
      this.rangeRow('بیشترین ورودی', cardY + 232, this.maxPrice, (v) => {
        this.maxPrice = v;
        this.render();
      }, enter);
    }

    this.countText = makeText('', { size: 24, fill: COLORS.textDim, align: 'center' });
    this.countText.anchor.set(0.5, 0);
    this.countText.position.set(DESIGN_W / 2, cardY + cardH + 14);
    this.addChild(this.countText);
    enter.push(this.countText);

    // ── Room list, fills down to the bottom button bar. ──
    const listY = cardY + cardH + 54;
    this.list = new ScrollList(LIST_PAGE.width, listHeight(listY, 104));
    this.list.position.set(LIST_PAGE.left, listY);
    this.addChild(this.list);
    enter.push(this.list);

    // Keep refresh prominent and thumb-reachable; Telegram's native back handles navigation.
    const refresh = new Button('بروزرسانی میزها', () => this.refresh(), {
      width: LIST_PAGE.width,
      height: 80,
      fontSize: 28,
      icon: this.ctx.iconTexture('refresh') ?? null,
      accessibleLabel: 'بروزرسانی فهرست میزهای بازی',
    });
    bottomBar([refresh], { left: LIST_PAGE.left, width: LIST_PAGE.width });
    this.addChild(refresh);
    enter.push(refresh);
    // Staggered entrance on first build only (not on every resize re-flow).
    if (!this.built) {
      this.built = true;
      enterFade(enter);
    }
    this.render();
  }

  /** A labeled range slider on the filters card; returns the value label (updated live + by the caller). */
  private rangeRow(
    name: string,
    y: number,
    value: number,
    onChange: (v: number) => void,
    enter: Container[],
  ): ReturnType<typeof makeText> {
    const label = makeText(`${name}: ${moneyFa(BigInt(Math.round(value)))}`, { size: 24, weight: '700', align: 'right' });
    label.anchor.set(1, 0.5);
    label.position.set(DESIGN_W - 44, y + 22);
    this.addChild(label);
    enter.push(label);
    const slider = new Slider({
      width: 340,
      min: 0,
      max: PRICE_MAX,
      step: PRICE_STEP,
      value,
      onChange: (v) => {
        label.text = `${name}: ${moneyFa(BigInt(Math.round(v)))}`;
        onChange(v);
      },
    });
    slider.position.set(44, y + 2);
    this.addChild(slider);
    enter.push(slider);
    return label;
  }

  private refresh(): void {
    this.ctx.client.send(encodeSearchGame());
  }

  private render(): void {
    const visible = this.entries.filter((e) => {
      if (!this.showRunning && e.game.gameState !== GameState.Wait) return false;
      if (this.gameFilter > 0 && e.game.gameName !== (this.gameFilter - 1)) return false;
      const p = Number(e.game.gamePrice);
      return p >= this.minPrice && p <= this.maxPrice;
    });
    const waiting = this.entries.filter((e) => e.game.gameState === GameState.Wait).length;
    const running = this.entries.filter(
      (e) => e.game.gameState === GameState.Running || e.game.gameState === GameState.Recovered,
    ).length;
    this.countText.text = `تعداد میز منتظر: ${toPersianDigits(waiting)} · تعداد میز پر: ${toPersianDigits(running)}`;
    this.list.setItems(visible.map((e) => this.card(e)));
  }

  private card(entry: SearchGameEntry): Container {
    const { game } = entry;
    const running = game.gameState !== GameState.Wait;
    // Fill the ScrollList's padded content area exactly. The previous extra 18px subtraction made
    // every row stop visibly short of the right edge and look clipped.
    const cw = this.list.contentWidth;
    const ch = 126;
    const card = new Container();
    // Keep a consistent identity color for each game. Waiting/running state remains visible through
    // the action label, icon, and button treatment instead of changing the card background.
    const gameColor = GAME_ROW_COLORS[game.gameName as GameName] ?? COLORS.panel;
    card.addChild(new Panel(cw, ch, { fill: gameColor }));

    const buttonX = 10;
    const buttonW = 164;
    const textLeft = buttonX + buttonW + 14;

    // Per-game icon, far right (gameIcon mirrors Unity's WaitListItem mapping).
    const iconSize = 68;
    const icon = iconSprite(this.ctx.gameIcon(game.gameName as GameName), iconSize);
    icon.position.set(cw - iconSize - 12, (ch - iconSize) / 2);
    card.addChild(icon);

    const textRight = cw - iconSize - 24;
    // Coin marks the price on line 1 (RTL leading edge).
    const coinSize = 28;
    const coin = iconSprite(this.ctx.iconTexture('coin'), coinSize);
    coin.position.set(textRight - coinSize, 22);
    card.addChild(coin);
    const lineRight = textRight - coinSize - 6;

    const variant = gameTypeLabel(game.gameName as GameName, game.gameType);
    const line1 = makeText(
      `${moneyFa(game.gamePrice)}  ${gameTimeLabel(game.gameTime)}  ${betOrRoundsLabel(game.gameName as GameName, game.gameCount)}${variant ? `  ${variant}` : ''}`,
      { size: 27, weight: '700' },
    );
    line1.anchor.set(1, 0);
    line1.position.set(lineRight, 20);
    this.fitRowText(line1, lineRight - textLeft);
    card.addChild(line1);

    const players = running
      ? `${entry.player1.username} در برابر ${entry.player2?.username ?? '—'}`
      : entry.player1.username;
    const line2 = makeText(players, { size: 24, fill: COLORS.textDim });
    line2.anchor.set(1, 0);
    line2.position.set(textRight, 74);
    this.fitRowText(line2, textRight - textLeft);
    card.addChild(line2);

    // Action button, left: gold «شروع بازی» (+play, join Wait) or secondary «جزئیات» (+eye, watch Running).
    const btn = new Button(
      running ? 'جزئیات' : 'شروع بازی',
      () => {
        if (running) {
          this.ctx.beginSpectating(game.uniqueId);
          this.ctx.client.send(encodeWatchGame(game.uniqueId));
        } else {
          this.ctx.client.send(encodeJoinGame(game.uniqueId));
        }
      },
      {
        width: buttonW,
        height: 80,
        fontSize: 24,
        icon: this.ctx.iconTexture(running ? 'eye' : 'play') ?? null,
        fill: running ? COLORS.panel : COLORS.accent,
        textColor: running ? COLORS.text : undefined,
      },
    );
    btn.position.set(buttonX, (ch - 80) / 2);
    card.addChild(btn);
    return card;
  }

  /** Keep long prices, variants, and usernames inside the row instead of letting the list mask cut them. */
  private fitRowText(text: ReturnType<typeof makeText>, maxWidth: number): void {
    if (text.width > maxWidth) text.scale.set(maxWidth / text.width);
  }
}
